import React from 'react';
import { Database, ExternalLink, LockKeyhole, Mail, ReceiptText, UserRound } from 'lucide-react';
import { appMeta } from '@/config/iphonefastflip';

const sections = [
  {
    id: 'zakres',
    icon: UserRound,
    title: '1. Jakie dane obsługuje aplikacja',
    paragraphs: [
      'Podczas zakładania i obsługi konta iPhoneFastFlip może przetwarzać adres e-mail, nazwę wyświetlaną, identyfikator konta oraz numer telefonu użyty do weryfikacji.',
      'Aplikacja zapisuje dane potrzebne do działania funkcji użytkownika: ulubione ogłoszenia, cenniki, ustawienia powiadomień, domyślną lokalizację, zakres cen, historię wyświetleń i dopasowane rekomendacje.',
      'Wersja webowa korzysta z pamięci przeglądarki do zachowania sesji, ustawień i stanu aplikacji. Serwer może otrzymywać standardowe dane techniczne połączenia, takie jak adres IP, typ przeglądarki i czas żądania.',
    ],
  },
  {
    id: 'cele',
    icon: Database,
    title: '2. W jakim celu dane są używane',
    paragraphs: [
      'Dane konta służą do uwierzytelnienia, synchronizacji ustawień między urządzeniami, zapisania ulubionych i cenników oraz obsługi kontaktu dotyczącego konta.',
      'Historia wyświetleń, preferencje i cenniki są używane do porządkowania ofert, obliczania wskaźników opłacalności i tworzenia rekomendacji w aplikacji.',
      'Dane techniczne mogą być wykorzystywane do zabezpieczenia usług, ograniczania nadużyć, diagnozowania błędów i utrzymania działania backendu.',
    ],
  },
  {
    id: 'platnosci',
    icon: ReceiptText,
    title: '3. Płatności i subskrypcje',
    paragraphs: [
      'Płatne plany są realizowane przez Stripe. iPhoneFastFlip przekazuje do Stripe dane wymagane do utworzenia sesji płatności i powiązania subskrypcji z kontem, w szczególności adres e-mail oraz wybrany plan.',
      'Dane karty i inne poufne dane płatnicze są wprowadzane na stronie Stripe i nie są przechowywane w kodzie aplikacji iPhoneFastFlip. Backend zapisuje identyfikatory oraz statusy potrzebne do obsługi aktywnego planu i portalu rozliczeniowego.',
    ],
  },
  {
    id: 'dostawcy',
    icon: ExternalLink,
    title: '4. Usługi zewnętrzne i źródła ogłoszeń',
    paragraphs: [
      'Do logowania, weryfikacji i przechowywania danych konta wykorzystywane są usługi Firebase. Do obsługi płatności wykorzystywany jest Stripe.',
      'Aplikacja prezentuje dane ogłoszeń pochodzące z OLX, Allegro Lokalnie i Vinted oraz umożliwia przejście do strony źródłowej. Po otwarciu takiego linku obowiązują zasady prywatności danego serwisu.',
      'Zakres i dostępność danych ogłoszeń mogą zmieniać się niezależnie od iPhoneFastFlip. Aplikacja nie jest oficjalnym produktem żadnego z wymienionych serwisów.',
    ],
  },
  {
    id: 'bezpieczenstwo',
    icon: LockKeyhole,
    title: '5. Bezpieczeństwo i okres przechowywania',
    paragraphs: [
      'Komunikacja z wdrożonymi usługami produkcyjnymi powinna odbywać się przez HTTPS. Dostęp do danych konta jest chroniony tokenem uwierzytelniającym, a operacje konta wymagają poprawnej sesji.',
      'Dane są przechowywane tak długo, jak jest to potrzebne do działania konta, realizacji subskrypcji, obsługi wymaganych rozliczeń albo zabezpieczenia roszczeń. Lokalne dane przeglądarki można usunąć w ustawieniach przeglądarki.',
      'Środowisko lokalne przeznaczone do testów nie powinno być udostępniane publicznie ani używane z rzeczywistymi danymi płatniczymi bez poprawnej konfiguracji zabezpieczeń.',
    ],
  },
  {
    id: 'prawa',
    icon: Mail,
    title: '6. Kontakt i decyzje dotyczące danych',
    paragraphs: [
      `W sprawach dotyczących dostępu, poprawienia lub usunięcia danych konta skontaktuj się z Alterolis pod adresem ${appMeta.supportEmail}. Przed wykonaniem żądania możemy poprosić o potwierdzenie tożsamości właściciela konta.`,
      'Jeżeli zakres działania aplikacji albo wykorzystywani dostawcy ulegną zmianie, treść tej informacji zostanie zaktualizowana. Data bieżącej wersji dokumentu znajduje się na początku strony.',
    ],
  },
] as const;

const Privacy: React.FC = () => (
  <main className="px-4 pb-24 pt-28 sm:pt-32">
    <div className="mx-auto max-w-6xl">
      <header className="mx-auto mb-14 max-w-3xl text-center">
        <p className="section-eyebrow">Prywatność</p>
        <h1 className="text-4xl font-black tracking-[-0.04em] text-white sm:text-6xl">Informacja o danych w iPhoneFastFlip</h1>
        <p className="mt-5 text-sm text-slate-500">Ostatnia aktualizacja: 18 lipca 2026 r.</p>
      </header>

      <div className="grid gap-8 lg:grid-cols-[240px_1fr]">
        <aside>
          <nav className="sticky top-24 rounded-2xl border border-white/[0.08] bg-white/[0.025] p-4" aria-label="Spis treści">
            <p className="mb-3 px-3 text-xs font-bold uppercase tracking-[0.18em] text-slate-600">Spis treści</p>
            {sections.map((section) => (
              <a key={section.id} href={`#${section.id}`} className="block rounded-lg px-3 py-2 text-sm text-slate-500 hover:bg-white/5 hover:text-white">
                {section.title.replace(/^\d+\. /, '')}
              </a>
            ))}
          </nav>
        </aside>

        <div className="space-y-5">
          <section className="rounded-2xl border border-amber-400/20 bg-amber-500/[0.06] p-5 text-sm leading-6 text-amber-100/75">
            Dokument opisuje aktualny sposób działania projektu. Przed publicznym uruchomieniem usługi należy uzupełnić dane administratora i zweryfikować treść pod kątem wymogów prawnych właściwych dla prowadzonej działalności.
          </section>
          {sections.map((section) => {
            const Icon = section.icon;
            return (
              <section key={section.id} id={section.id} className="scroll-mt-24 rounded-[26px] border border-white/[0.08] bg-white/[0.025] p-6 sm:p-8">
                <div className="mb-5 flex items-center gap-4">
                  <div className="grid h-11 w-11 place-items-center rounded-xl bg-blue-500/10 text-blue-300">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h2 className="text-xl font-bold text-white sm:text-2xl">{section.title}</h2>
                </div>
                <div className="space-y-4 text-sm leading-7 text-slate-400 sm:text-base">
                  {section.paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
                </div>
              </section>
            );
          })}
        </div>
      </div>
    </div>
  </main>
);

export default Privacy;
